import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { File as _File } from "src/graphql/generated/graphql";
import { uploadImageMutation } from "src/graphql/operations/files/mutations";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { handleError } from "src/utils/error";
import { getAsByteArray } from "src/utils/getAsByteArray";

export const useUploadImage = () => {
    const { t } = useTranslation(["common"]);

    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const uploadImage = useCallback(
        async (image: File): Promise<_File> => {
            setIsUploadingImage(true);

            try {
                const response = await graphqlFetch(uploadImageMutation, {
                    filename: image.name,
                });

                const presignedUploadUrl = response.uploadImage.presignedUploadUrl;

                const fileByteArray = await getAsByteArray(image);

                await fetch(presignedUploadUrl, {
                    method: "PUT",
                    body: fileByteArray,
                });

                showNotification({
                    title: t("success"),
                    message: t("success_uploading_image"),
                    color: "green",
                    icon: null,
                });

                return response.uploadImage;
            } catch (err) {
                handleError(err, {
                    message: { key: "error_uploading_image", namespace: "common" },
                });
            } finally {
                setIsUploadingImage(false);
            }
        },
        [t]
    );

    return {
        isUploadingImage,
        uploadImage,
    };
};
